import { apiCall, default as API_CONFIG } from '../config/api';

/**
 * Browser-side plumbing for push notifications: feature detection, the
 * service worker, and the PushManager subscription. UI state lives in
 * hooks/usePushNotifications.js.
 */

const SW_URL = '/push-sw.js';

const isIos = () =>
  /iphone|ipad|ipod/i.test(navigator.userAgent) ||
  // iPadOS reports itself as a Mac.
  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

const isStandalone = () =>
  window.matchMedia?.('(display-mode: standalone)')?.matches || window.navigator.standalone === true;

/**
 * What this browser can do. On iPhone/iPad, push only exists inside the
 * home-screen app (iOS 16.4+), so Safari in a tab reports unsupported with
 * needsInstall set.
 */
export const pushSupport = () => {
  const supported =
    'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
  return {
    supported,
    needsInstall: !supported && isIos() && !isStandalone(),
  };
};

export const notificationPermission = () =>
  'Notification' in window ? Notification.permission : 'unsupported';

/** Registers the worker (idempotent) and waits until it is active. */
export const registerPushWorker = async () => {
  if (!('serviceWorker' in navigator)) return null;
  await navigator.serviceWorker.register(SW_URL, { scope: '/' });
  return navigator.serviceWorker.ready;
};

export const currentSubscription = async () => {
  if (!pushSupport().supported) return null;
  const reg = await navigator.serviceWorker.getRegistration('/');
  return reg ? reg.pushManager.getSubscription() : null;
};

/**
 * "iPhone (home screen app)", "Samsung Internet on Android (installed app)",
 * "Chrome on Windows" -- shown in the Settings device list.
 */
export const deviceLabel = () => {
  const ua = navigator.userAgent;
  const device =
    /iphone/i.test(ua) ? 'iPhone' :
    /ipad/i.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1) ? 'iPad' :
    /android/i.test(ua) ? 'Android' :
    /windows/i.test(ua) ? 'Windows' :
    /mac os/i.test(ua) ? 'Mac' :
    /linux/i.test(ua) ? 'Linux' : 'Unknown device';

  if (device === 'iPhone' || device === 'iPad') {
    return isStandalone() ? `${device} (home screen app)` : device;
  }

  // Order matters: Samsung Internet and Edge both also claim "Chrome".
  const browser =
    /samsungbrowser/i.test(ua) ? 'Samsung Internet' :
    /edg(e|a)?\//i.test(ua) ? 'Edge' :
    /firefox|fxios/i.test(ua) ? 'Firefox' :
    /chrome|crios/i.test(ua) ? 'Chrome' :
    /safari/i.test(ua) ? 'Safari' : 'Browser';
  return `${browser} on ${device}${isStandalone() ? ' (installed app)' : ''}`;
};

const urlBase64ToUint8Array = (base64Url) => {
  const padding = '='.repeat((4 - (base64Url.length % 4)) % 4);
  const base64 = (base64Url + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  return Uint8Array.from(raw, (c) => c.charCodeAt(0));
};

const sameKey = (buffer, base64Url) => {
  if (!buffer) return false;
  const a = new Uint8Array(buffer);
  const b = urlBase64ToUint8Array(base64Url);
  return a.length === b.length && a.every((v, i) => v === b[i]);
};

export const saveSubscription = (subscription) =>
  apiCall(API_CONFIG.endpoints.pushSubscriptions, {
    method: 'POST',
    body: JSON.stringify({ ...subscription.toJSON(), label: deviceLabel() }),
  });

/**
 * Asks permission, subscribes this browser and registers it with the
 * backend. Call straight from a click handler: Safari only shows the
 * permission prompt in direct response to a user gesture, so nothing
 * slow (like a network call) may run before requestPermission().
 */
export const enablePush = async (publicKey) => {
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    throw new Error(
      permission === 'denied'
        ? 'Notifications are blocked for this site. Allow them in the browser or OS settings, then try again.'
        : 'Notification permission was not granted.'
    );
  }

  const reg = await registerPushWorker();
  let subscription = await reg.pushManager.getSubscription();

  // A subscription made under an old server key can never receive pushes
  // signed with the new one. Replace it.
  if (subscription && !sameKey(subscription.options?.applicationServerKey, publicKey)) {
    await subscription.unsubscribe();
    subscription = null;
  }

  if (!subscription) {
    subscription = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    });
  }

  await saveSubscription(subscription);
  return subscription;
};

/**
 * Unsubscribes this browser. The caller removes the backend row; doing it
 * in that order means a failed DELETE leaves a row the next push prunes
 * (410 Gone), never a live subscription the backend has forgotten.
 */
export const disablePush = async () => {
  const subscription = await currentSubscription();
  if (subscription) await subscription.unsubscribe();
};
